import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'
import RelatedTreatments from '../components/related-treatments'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '30-60 minutes',
  session: '1 session',
  results: 'Results 6-12 months',
  cost: 'From £115',
  downtime: 'Minimal',
}

const relatedTreatments = [
  {
    name: 'Lip Fillers Cheshunt',
    slug: 'lip-fillers',
    description:
      'Compare Russian Lips with classic lip fillers for the perfect shape and volume.',
  },
  {
    name: 'Cheek Fillers Cheshunt',
    slug: 'cheek-fillers',
    description:
      'Frame your new lips with sculpted cheekbones and a lifted mid-face.',
  },
  {
    name: 'Dermal Fillers Cheshunt',
    slug: 'dermal-fillers-cheshunt',
    description:
      'Enhance overall facial balance with dermal fillers for cheeks, chin and jawline.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much do Russian Lips cost in Cheshunt?',
    answer:
      'Russian lip treatments at Ada Aesthetics in Cheshunt start from £115 for 0.5ml and £149.99 for 1.1ml of premium Revolax filler. This specialist technique uses similar pricing to classic lip fillers. The final cost depends on the amount of product needed and your desired result. Book a free consultation at our Cheshunt clinic to discuss your goals and receive a personalized quote.',
  },
  {
    question: 'What is the difference between Russian Lips and Classic Lips?',
    answer:
      'The main difference is the side profile. Classic Lips protrude outwards and balance out a strong nose or chin. Go easy on the volume, though - too much can lead to the dreaded trout pout. If someone has delicate features, they will suit Russian Lips, which look flat from the side. This technique lifts the cupids bow and adds height to a thin top lip. Our Cheshunt practitioners are experts in both techniques.',
  },
  {
    question: 'Does Russian Lip work differently?',
    answer:
      'We use the same Hyaluronic Acid filler but a different technique. Tiny droplets of filler are injected vertically from the inner lips to the outer lip border. This technique lifts the lips to avoid projection. Most of the injections are into the middle of the lips for a beautiful heart-shaped look. In contrast, Classic Lips are injected horizontally from the lip border inwards. At Ada Aesthetics in Cheshunt, we will help you choose the right technique.',
  },
  {
    question: 'Does Russian Lip hurt more than Classic Lip?',
    answer:
      'Most dermal fillers contain a local anaesthetic, and your experienced Ada Aesthetics practitioner in Cheshunt will ensure your treatment is as comfortable as possible. Russian Lips are less painful than Classic Lips because we use fewer injections and only one in the sensitive lip border. However, there is more risk of swelling and bruising. Results should settle after two weeks.',
  },
  {
    question: 'How long do Russian Lips last?',
    answer:
      'Russian lip fillers typically last 12 months, similar to classic lip fillers. At our Cheshunt clinic, we use premium hyaluronic acid products for long-lasting, beautiful results. We recommend maintenance appointments to keep your Russian lips looking their best.',
  },
  {
    question: 'Am I suitable for Russian Lips?',
    answer:
      'Russian Lips are perfect if you have delicate facial features, a thin upper lip, or want a subtle, heart-shaped look without projection. During your free consultation at Ada Aesthetics in Cheshunt, we will assess your facial structure and recommend the best technique for you.',
  },
  {
    question: 'What is the aftercare for Russian Lips?',
    answer:
      'Aftercare for Russian Lips is similar to classic fillers. For 24 hours after treatment at our Cheshunt clinic, avoid strenuous exercise, alcohol, extreme heat, and touching your lips. Expect some swelling for up to 2 weeks. We provide comprehensive aftercare instructions.',
  },
  {
    question: 'Can Russian Lips be reversed?',
    answer:
      'Yes! Because we use hyaluronic acid fillers at Ada Aesthetics in Cheshunt, Russian Lips can be dissolved using hyaluronidase if you are not happy with the results. However, most of our Cheshunt clients love their natural, heart-shaped Russian lips.',
  },
]

const RussianLips: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Russian Lips in Cheshunt',
    'Specialized Russian lip technique creating heart-shaped, naturally elevated lips. Unique vertical injection method for delicate features in Cheshunt, Hertfordshire. Expert Russian lip treatments.',
    '200'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Russian Lips Cheshunt', url: 'https://adaaesthetics.uk/russian-lips' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Russian Lips Cheshunt | Heart-Shaped Lip Technique | Ada Aesthetics"
        description="Expert Russian lip technique in Cheshunt. Heart-shaped, naturally elevated lips using vertical injection method. Perfect for petite features. Book: 07478705252"
        keywords="Russian lips Cheshunt, Russian lip technique, heart shaped lips, vertical lip fillers, petite lip enhancement, Ada Aesthetics Hertfordshire"
        url="https://adaaesthetics.uk/russian-lips"
        type="service"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax
        titleName="Russian flat lips"
        customClass="page_title"
      />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Get Stunning Heart-Shaped Lips with the Russian Lips Technique in Cheshunt</h1>
              <p className="excerpt">
                Dreaming of that coveted doll-like pout with a perfectly defined cupid's bow? The Russian lips technique creates the most beautiful heart-shaped lips with elegant lift and zero projection. At Ada Aesthetics in Cheshunt, our specialist practitioners master this advanced method to give you stunning, naturally elevated lips that suit your delicate features. Serving Cheshunt, Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <h2>What Are Russian Lips?</h2>
              <p>
                Do you have a thin top lip? Petite features? The Classic Lip might not be the right shape for your
                side profile; Russian Lips might suit you better. Take a tip from Russian women - they understand
                good lips. Just imagine the heart-shaped lips of a Russian doll.
              </p>
              <p>
                Russian Lips are a specialized lip filler technique that creates a subtle, elevated look with a
                beautiful heart shape when viewed from the front. Unlike classic lip fillers that add projection
                and fullness, Russian Lips create height and definition while maintaining a flat profile from the side.
              </p>

              <h2>Russian Lips vs Classic Lips - What is the Difference?</h2>
              <p>At Ada Aesthetics in Cheshunt, we offer both techniques and will help you choose the right one:</p>

              <h3>Classic Lips</h3>
              <ul>
                <li>Horizontal injection technique from border to center</li>
                <li>Creates outward projection and fullness</li>
                <li>Suits strong features (prominent nose or chin)</li>
                <li>More voluminous from the side</li>
                <li>Traditional full lip look</li>
              </ul>

              <h3>Russian Lips</h3>
              <ul>
                <li>Vertical injection technique from base to border</li>
                <li>Creates lift and height without projection</li>
                <li>Suits delicate, petite features</li>
                <li>Flat appearance from the side</li>
                <li>Heart-shaped, doll-like appearance</li>
                <li>Emphasizes the cupid bow</li>
              </ul>

              <h2>How Does the Russian Lip Technique Work?</h2>
              <p>
                At our Cheshunt clinic, we use the same premium hyaluronic acid fillers as classic lip treatments,
                but the injection technique is completely different. Here is what makes Russian Lips unique:
              </p>
              <ul>
                <li><strong>Vertical injection pattern</strong> - tiny droplets injected from the inner lip upwards to the outer border</li>
                <li><strong>Focused on the middle</strong> - most product goes into the center for the heart shape</li>
                <li><strong>Minimal border injections</strong> - only one injection in the sensitive lip border</li>
                <li><strong>Lifts rather than projects</strong> - creates height instead of outward fullness</li>
                <li><strong>Precise technique</strong> - requires specialist training and experience</li>
              </ul>

              <h2>Benefits of Russian Lips in Cheshunt</h2>
              <p>Why choose Russian Lips at Ada Aesthetics?</p>
              <ul>
                <li><strong>Natural-looking height</strong> without excessive volume</li>
                <li><strong>Perfect for petite faces</strong> - enhances delicate features</li>
                <li><strong>Beautiful heart shape</strong> - coveted doll lip appearance</li>
                <li><strong>Flattering side profile</strong> - no duck lip projection</li>
                <li><strong>Less painful</strong> - fewer injections than classic technique</li>
                <li><strong>Enhances cupid bow</strong> - creates definition and lift</li>
                <li><strong>Ideal for thin upper lips</strong> - adds height and presence</li>
                <li><strong>Long-lasting results</strong> - up to 12 months</li>
              </ul>

              <h2>Your Russian Lips Treatment Journey in Cheshunt</h2>

              <h3>1. Expert Consultation</h3>
              <p>
                Your Russian lip journey begins with a detailed consultation at our Cheshunt clinic. We will assess
                your facial structure, discuss your aesthetic goals, and determine if Russian Lips or Classic Lips
                (or a combination) will best suit your features.
              </p>

              <h3>2. The Specialist Treatment</h3>
              <p>
                The Russian lip technique takes approximately 60 minutes at Ada Aesthetics in Cheshunt. Our experienced
                practitioners use ultra-fine needles to inject tiny amounts of hyaluronic acid vertically into precise
                points. The filler contains local anaesthetic for comfort during the procedure.
              </p>

              <h3>3. Managing Swelling</h3>
              <p>
                Russian Lips can cause more initial swelling than classic fillers due to the vertical injection technique.
                This is completely normal. Swelling typically peaks at 48 hours and settles within 2 weeks. Your beautiful
                heart-shaped lips will emerge as the swelling subsides.
              </p>

              <h3>4. Stunning Results</h3>
              <p>
                Once settled, your Russian lips will reveal a gorgeous heart shape with elevated cupid bow, subtle
                height, and natural-looking definition. Results last up to 12 months with proper maintenance.
              </p>

              <h2>Why Choose Ada Aesthetics for Russian Lips in Cheshunt?</h2>
              <ul>
                <li><strong>Specialist training</strong> in advanced Russian lip technique</li>
                <li><strong>Artistic eye</strong> - we understand facial proportions and balance</li>
                <li><strong>Premium hyaluronic acid</strong> - FDA-approved, safe products</li>
                <li><strong>Natural results</strong> - heart-shaped lips that suit your face</li>
                <li><strong>Free consultations</strong> to assess suitability</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
                <li><strong>Serving local areas</strong> including Waltham Cross, Broxbourne, Hoddesdon</li>
                <li><strong>Competitive pricing</strong> with transparent costs</li>
              </ul>

              <h2>Am I Suitable for Russian Lips?</h2>
              <p>Russian Lips are ideal if you:</p>
              <ul>
                <li>Have delicate or petite facial features</li>
                <li>Want height and lift rather than volume</li>
                <li>Have a thin upper lip you would like to enhance</li>
                <li>Prefer a subtle, heart-shaped look</li>
                <li>Do not want visible projection from the side</li>
                <li>Love the doll lip aesthetic</li>
              </ul>
              <p>
                At Ada Aesthetics in Cheshunt, we have an artistic eye and understand that different face shapes suit
                different lip shapes. We promise that your new lips will be in perfect proportion with the rest of
                your face. It is time to feel more confident about your smile!
              </p>

              <h2>Russian Lips for Cheshunt Residents</h2>
              <p>
                Located at 15 Ousden Drive, Cheshunt, EN8 9RL, Ada Aesthetics is your local specialist for Russian
                lip treatments. We are easily accessible for residents throughout Hertfordshire, including Cheshunt,
                Waltham Cross, Broxbourne, Hoddesdon, Waltham Abbey, and Enfield.
              </p>
              <p>
                No need to travel to London for specialist Russian lip treatments - expert aesthetic care with advanced
                techniques is available right here in Cheshunt.
              </p>

              <h2>Book Your Russian Lips Consultation in Cheshunt</h2>
              <p>
                Ready for beautiful, heart-shaped Russian lips? Join 500+ clients who trust us for this popular technique.
                Schedule your free consultation today - our specialist practitioners will assess your features and create a personalized
                treatment plan to achieve your perfect lip aesthetic.
              </p>
              <p>
                <strong>Limited availability for Russian lips this month</strong> - Call us today at <strong>07478705252</strong> or book online to secure your appointment.
                Your complimentary consultation (worth £50) includes expert lip assessment and personalized design plan.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Russian Lips Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>

          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default RussianLips

export const pageQuery = graphql`
  query RussianLips {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
